import { internalActions } from "@/action/internalActions";
import type { ALMIssue } from "@/backend/natsstubs/almsync/common/models";
import { getStickies } from "@/backend/read/BackendReader";
import { addSticky } from "@/backend/read/changeSticky";
import { mapUserId } from "@/backend/read/mapBackendData";
import type { BoardCard, SearchResult } from "@/model/card";
import type { IssueSelectorCard } from "@/model/issueSelector";
import { useBoardsStore } from "@/store/boards";
import { useLinkStore } from "@/store/link";
import { useStickyTypeStore } from "@/store/stickyType";

/**
 * Converts a SearchResult to IssueSelectorCard format
 * Assumes the SearchResult is a backlog item
 */
export function issueSelectorCardFromSearchResult(result: SearchResult): IssueSelectorCard {
  const type = useStickyTypeStore().findStickyType(result.typeId);

  return {
    id: result.id,
    type,
    almId: result.almId,
    almIssueUrl: result.almIssueUrl, // Backlog items don't have external URLs
    text: result.text,
    points: result.priority ?? 0,
    assignee: undefined, // No assignee on backlog items
  };
}

/**
 * Converts an ALMIssue to IssueSelectorCard format
 * Assumes the ALMIssue doesn't exist in the app yet
 */
export function issueSelectorCardFromAlmIssue(issue: ALMIssue): IssueSelectorCard {
  return {
    id: undefined, // ALM Issues don't have an in-app sticky id
    type: useStickyTypeStore().findTypeByAlmType(issue.almTypeId),
    almId: issue.issueId,
    almIssueUrl: issue.issueUrl,
    almSourceId: issue.sourceId,
    text: issue.summary,
    points: issue.storyPoints ?? 0,
    assignee: mapUserId(issue.assignee?.pipId, issue.assignee?.almId),
  };
}

/**
 * Gets backlog cards for highlighting purposes.
 * Checks backlog board first, loads missing cards from backend.
 */
async function getOrLoadBacklogCards(cardIds: string[]): Promise<BoardCard[]> {
  const backlogBoard = useBoardsStore().boardByType("backlog");
  const result: BoardCard[] = [];
  const missingIds: string[] = [];

  // First pass: collect existing cards, identify missing
  for (const id of cardIds) {
    const boardCard = backlogBoard.cards[id];
    if (boardCard) {
      result.push(boardCard);
    } else {
      missingIds.push(id);
    }
  }

  // Load missing from backend
  if (missingIds.length > 0) {
    const stickies = await getStickies(missingIds, true);
    stickies.forEach((s) => {
      addSticky({ ...s, shouldAnimate: false });
    });

    // Collect newly loaded cards
    for (const id of missingIds) {
      const boardCard = backlogBoard.cards[id];
      if (boardCard) {
        result.push(boardCard);
      }
    }
  }

  return result;
}

// Request ID guard to prevent race conditions from out-of-order async results
let latestHighlightRequestId = 0;

/**
 * Highlights cards linked to the given backlog cards.
 * Loads cards from the backend if not already on the board.
 */
export async function highlightLinkedCards(cardIds: string[]): Promise<void> {
  const requestId = ++latestHighlightRequestId;
  const cards = await getOrLoadBacklogCards(cardIds);

  // Only update marks if this is still the most recent request
  if (requestId !== latestHighlightRequestId) {
    return;
  }

  if (cards.length > 0) {
    internalActions.markCardLinkedCards(cards);
  } else {
    useLinkStore().removeAllMarks();
  }
}
